import { OnboardingLayout, useSlideSetup } from '@/features/client-onboarding';
import { D2cPaywallOffer } from '@/features/client-onboarding/components/d2c-paywall-offer';
import { useIsAgeIneligibleForQuotes } from '@/features/client-onboarding/hooks/use-is-age-ineligible-for-quotes';
import { OnboardingInsightsSummary } from '@/pages/OnboardingInsightsSummary';
import { OnboardingCostFactors } from '@/pages/OnboardingCostFactors';
import { D2CCheckoutPage } from '@/pages/D2CCheckoutPage';
import { OnboardingD2CVirtualMeeting } from '@/pages/OnboardingD2CVirtualMeeting';
import { useNavigateToPage } from '@/Router';
import { selectSession } from '@/model';
import { useSelector } from 'react-redux';
import { Navigate } from 'react-router-dom';
import { useIsConsumerFlowEnabled } from '@/hooks/use-app-mode';
import { useConsumerFlowContinuation, useEndGuidedJourney } from '@/features/consumer';
import { Spinner } from '@design-system';
import { Eye, Heart, TrendingUp } from 'lucide-react';
import { analytics } from '@shared/posthog/analytics';

const VALUE_PROPS = [
  {
    icon: <Eye className="text-primary size-5" />,
    title: 'Understand what care may cost',
    description: 'Fit your plan to your needs',
  },
  {
    icon: <Heart className="text-primary size-5" />,
    title: 'How to reduce stress on loved ones',
    description: 'Comprehensive care planning and family involvement predictions',
  },
  {
    icon: <TrendingUp className="text-primary size-5" />,
    title: 'Access funding strategies',
    description: 'Analyze any assets, income, or insurance policy and model how it fits your plan',
  },
];

// Wherever the guided consumer flow is enabled, it takes the paywall's place.
export function OnboardingD2CPaywall({ clientId }: ClientIdProps) {
  const isConsumerFlowEnabled = useIsConsumerFlowEnabled();
  if (isConsumerFlowEnabled) return <ConsumerFlowHandOff clientId={clientId} />;
  return <D2CPaywall clientId={clientId} />;
}

// Arriving here by Next from the summary is walking the results journey. A consumer who has already
// finished their plan is updating it, so they continue at the first consumer step with their saved
// answers rather than being sent back to Your Plan; anyone else goes to the flow entry.
function ConsumerFlowHandOff({ clientId }: ClientIdProps) {
  const { isLoading, path } = useConsumerFlowContinuation(clientId, { enabled: true });
  useEndGuidedJourney(clientId);
  if (isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <Spinner className="size-6" />
      </div>
    );
  }
  return <Navigate to={path} replace />;
}

function D2CPaywall({ clientId }: ClientIdProps) {
  const navigateToPage = useNavigateToPage();
  const { clientStripe } = useSelector(selectSession);
  const isAgeIneligible = useIsAgeIneligibleForQuotes();

  const hasActivePlan = clientStripe?.hasActivePlan ?? false;

  useSlideSetup({
    slidePage: OnboardingD2CPaywall,
    nextButtonLabel: 'Next',
    nextSlidePage: hasActivePlan ? OnboardingCostFactors : undefined,
    previousSlidePage: OnboardingInsightsSummary,
    shouldHideNextNavigation: !hasActivePlan,
  });

  const handleAccessClick = () => {
    analytics.d2c.accessClicked({ clientId });
    navigateToPage(D2CCheckoutPage, { clientId });
  };

  const handleVirtualMeetingClick = () => {
    analytics.d2c.virtualMeetingClicked({ clientId });
    navigateToPage(OnboardingD2CVirtualMeeting, { clientId });
  };

  return (
    <OnboardingLayout>
      <D2cPaywallOffer
        isAgeIneligible={isAgeIneligible}
        valuePropsTitle="Why families go deeper with Waterlily"
        valueProps={
          <div className="space-y-6">
            {VALUE_PROPS.map(prop => (
              <div
                key={prop.title}
                className="flex gap-3"
              >
                <div className="bg-primary/10 flex size-10 shrink-0 items-center justify-center rounded-full">
                  {prop.icon}
                </div>
                <div>
                  <h3 className="font-semibold text-gray-950">{prop.title}</h3>
                  <p className="text-foreground mt-0.5 text-sm">{prop.description}</p>
                </div>
              </div>
            ))}
          </div>
        }
        banner={
          <div className="rounded-lg bg-green-50 px-3 py-2 text-center">
            <p className="text-sm font-semibold text-green-800">🎉 Limited time only — 50% off</p>
          </div>
        }
        primaryAction={{ label: 'Access Now — $9.99', onClick: handleAccessClick }}
        secondaryAction={{ label: 'Access via Virtual Meeting — Free', onClick: handleVirtualMeetingClick }}
        priceLine={
          <>
            <span className="text-gray-400 line-through">$19.99</span>{' '}
            <span className="font-semibold text-gray-950">$9.99</span> one-time payment for lifetime
            access. No recurring charges.
          </>
        }
      />
    </OnboardingLayout>
  );
}
